import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X, CircleDot } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';

const KIND_META = {
  arco_request_new:      { color: '#DC2626', label: 'Nueva solicitud ARCO+' },
  arco_request_verified: { color: '#7C3AED', label: 'ARCO+ verificada' },
  task_completed:       { color: '#2A9B68', label: 'Tarea completada' },
  document_published:   { color: '#2563EB', label: 'Documento aprobado' },
  document_implemented: { color: '#065F46', label: 'Documento implementado' },
  invite_accepted:      { color: '#7C3AED', label: 'Nuevo miembro' },
  system_created:       { color: '#0891B2', label: 'Sistema registrado' },
  reminder:             { color: '#F59E0B', label: 'Recordatorio' },
  generic:              { color: '#6B7280', label: 'Notificación' },
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/notifications?limit=15');
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000); // refresh cada 45s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const openItem = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_e) { /* silent */ }
    }
    if (n.action_url) navigate(n.action_url);
    setOpen(false);
    load();
  };

  const markAll = async () => {
    await api.post('/notifications/read-all');
    load();
  };

  return (
    <div className="relative" ref={ref} data-testid="notifications-bell">
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="pv-btn-secondary flex items-center gap-2 relative"
        title="Notificaciones"
        data-testid="notifications-toggle"
      >
        <Bell size={14} strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#DC2626] text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-white border border-[#E5E7EB] shadow-lg z-50 flex flex-col max-h-[80vh]" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <div className="text-[13px] font-semibold text-[#1C2329]">Notificaciones</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} className="text-[11px] text-[#2A9B68] hover:text-[#228256] flex items-center gap-1" data-testid="notifications-read-all">
                  <CheckCheck size={11} /> Marcar todas
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-[#6B7280] hover:text-[#1C2329]">
                <X size={13} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-12 text-center text-[12.5px] text-[#6B7280]">
                <Bell size={20} className="mx-auto text-[#D1D5DB] mb-2" strokeWidth={1.5} />
                Sin notificaciones aún.
              </div>
            ) : (
              <ul className="divide-y divide-[#F3F4F6]">
                {items.map((n) => {
                  const km = KIND_META[n.kind] || KIND_META.generic;
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => openItem(n)}
                        className={`w-full text-left px-4 py-3 hover:bg-[#FAFBFC] transition-colors flex items-start gap-2.5 ${!n.is_read ? 'bg-[#FEFCE8]' : ''}`}
                        data-testid={`notif-${n.id}`}
                      >
                        <div className="mt-1 shrink-0">
                          {!n.is_read ? (
                            <CircleDot size={10} strokeWidth={2.5} style={{ color: km.color }} />
                          ) : (
                            <div className="w-2.5 h-2.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-medium mb-0.5" style={{ color: km.color }}>
                            {km.label}
                          </div>
                          <div className={`text-[13px] leading-snug ${!n.is_read ? 'text-[#1C2329] font-medium' : 'text-[#374151]'}`}>
                            {n.title}
                          </div>
                          {n.message && (
                            <div className="text-[11.5px] text-[#6B7280] mt-0.5 truncate">{n.message}</div>
                          )}
                          <div className="text-[10.5px] text-[#9CA3AF] mt-1 font-mono">{formatDateTime(n.created_at)}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
